import { trans } from 'matice';
import { toastBus } from '@/toast.js';
import moment from 'moment';
import * as mdi from '@mdi/js';

export const handleErrors = (e) => {
    let handled = false;
    const data = e.data??{};
    let status = e.response ? e.response.status : (e?.status ?? -1);
    if (status === 403) {
        toastBus.emit('add', {
            severity: 'error',
            detail: trans('global.error_access'),
            summary: trans('global.error'),
            life: 3000,
        });
        // stop here: the generic message/trace path below would emit a SECOND toast (exposing the
        // raw "no access #..." detail) and, for api.js error bodies without a .url, throw at
        // response.url.match(...). The access error is fully handled.
        return true;
    }
    let error = e.message ?? data.message ?? '';
    if (isObject(error)) {
        error = Object.values(error);
    }
    if (Array.isArray(error)) {
        error = error.join('\n');
    }
    const errorNo = !e.message ? parseInt(error.substring(error.length - 3)) : 'x';
    let detail = error.replace('<br>', '\n');
    if (!isNaN(errorNo)) {
        detail = trans('error.E' + errorNo) + ' ' + detail;
    }
    const response = e.response ? e.response : e;
    let header = '';
    if ((response?.redirected && response?.url?.includes('login')) || detail.match('Unauthenticated')) {
        detail = trans('auth.nologin');
        setTimeout(() => window.location.href='/', 3500);
        handled = true;
        status = 401;
    } else if ((status != 200) && response?.url?.match(/localhost/)) {
        header = detail + (e.file ? '\n' + e.file + '::' + e.line : '');
        detail = '<span class=\'trace\'><span>';
        const spacer = '&nbsp;';
        const trace = e.trace;
        if (trace && (trace.length > 0)) {
            for (let i = 0; i < 10; i++) {
                detail += spacer.repeat(i * 4) + '-> ' + trace[i]['class'] + trace[i]['type'] + trace[i]['function'] + '()';
                detail += '\n' + spacer.repeat(i * 4) + trace[i]['file'] + '::' + trace[i]['line'] + '\n';
            }
        } else {
            Object.keys(data).forEach((key) => {
                if (key == 'trace') {
                    for (let i = 0; i < 5; i++) {
                        Object.keys(data[key][i]).forEach((subkey, index) => {
                            detail += ((index == 0) ? '&bull;' : '&nbsp;') + '&nbsp;&nbsp;&nbsp;' + subkey + ':' + data[key][i][subkey] + '\n';
                        });
                    }
                } else {
                    detail += key + ': ' + data[key] + '\n';
                }
            });
        }
        console.log('Error: ', error, e, detail, data);
    }
    if (handled || (status != 200)) {
        toastBus.emit('add', {
            severity: 'error',
            detail: detail.split('\n').join('<br>'),
            summary:  trans('global.error') + ': ' + header,
            sticky: true,
        });
    }
    return handled;
};

export const arrayToLines = (data, prefix = '') => {
    data = data ? (!Array.isArray(data) ? data.split('\n') : data) : [];
    return (data.length ? prefix : '-') + data.join('<br>' + prefix);
};

export const navigateTo = (url) => {
    document.location.href = url;
};

/**
 * Returns the route name prefix (e.g. integeri, backend, or empty for user dashboard).
 */
export const resolveRoutePrefix = (route, pageUrl = '') => {
    const current = route().current();
    if (current) {
        return current.split('.')[0];
    }
    const path = String(pageUrl).split('?')[0];
    if (path.startsWith('/integeri')) {
        return 'integeri';
    }
    if (path.startsWith('/backend')) {
        return 'backend';
    }
    return '';
};

export const formatVideoLengthTime = (node, length) => {
    const getContentLength = (node) => {
        if (node.videoLength) {
            return node.videoLength;
        } else if (node.slideLength) {
            return node.slideLength;
        } else {
            return 0;
        }
    };
    if (node != null) {
        let totalChilds = 0 ;
        const seconds = getContentLength(node);
        if (node.children) {
            node.children.forEach(element => {
                totalChilds += element.totalLength;
            });
        }
        if (node.parent_id == null) {
            return formatTime(node.totalLength);
        } else {
            if (totalChilds > 0) {
                return formatTime(seconds) + (totalChilds ? ' ( + ' + formatTime(totalChilds) + ')' : '');
            } else {
                return formatTime(node.totalLength);
            }
        }
    }
    else {
        return formatTime(length);
    }
};

export const calcVideoLength = (node) => {
    let total = 0;
    if (Array.isArray(node.children)) {
        node.children.forEach((child) => {
            total += calcVideoLength(child);
        });
    }
    let videos = node.videos;
    let slides = node.slides;
    let videoLength = 0, slideLength = 0;
    if (!videos) {
        videos = node.content_node?.videos;
    }
    if (videos && (videos.length > 0)) {
        videos.forEach((video) => {
            videoLength += video.video_s3_length;
        });
    } else {
        if (!slides) {
            slides = node.content_node?.slides;
        }
        if (slides && (slides.length > 0)) {
            slides.forEach((slide) => {
                slideLength += slide.slide_length;
            });
        }
    }
    // from stepData
    if (!videos && !slides) {
        if (node.isVideo) {
            videoLength = node.videosLength;
        } else if (node.isSlide) {
            slideLength = node.slidesLength;
        }
    }
    total += videoLength + slideLength;
    node.videoLength = videoLength;
    node.slideLength = slideLength;
    node.totalLength = total;
    return total;
};

const splitSeconds = (seconds) => {
    const days = Math.floor(seconds / 3600 / 24);
    const hours = Math.floor(seconds / 3600) % 24;
    const minutes = Math.floor(seconds / 60) % 60;
    const secs = Math.trunc(seconds % 60);
    return [days, hours, minutes, secs];
};

export const formatVideoDuration = (seconds) => {
    const [hours, minutes, secs] = splitSeconds(seconds);
    return [hours, minutes, secs]
        .map(v => v < 10 ? '0' + v : v)
        .join(':');
};

export const formatTime = (seconds, opt = {}) => {
    opt = Object.assign({}, {displayOnEmpty: '', format: 'hms', short: false, decimal: false}, opt);
    const negative = seconds < 0 ? '-' : '';
    let [days, hours, minutes, secs] = splitSeconds(Math.abs(seconds));
    const hasDays    = opt.format.indexOf('d') > -1;
    const hasHours   = opt.format.indexOf('h') > -1;
    const hasMinutes = opt.format.indexOf('m') > -1;
    const hasSeconds = opt.format.indexOf('s') > -1;
    if (!hasDays) {
        hours = hours + (days * 24);
    }
    if (!hasHours) {
        minutes = minutes + (hours * 60);
    }
    if (!hasMinutes) {
        // rounding
        if ((minutes > 30) && hasHours) {
            hours = hours + 1;
            if ((hours >= 24) && hasDays) {
                days = days + 1;
                hours = hours - 24;
            }
        }
        secs = secs + (minutes * 60);
    }
    if (!hasSeconds) {
        // rounding
        if (secs > 30) {
            minutes = minutes + 1;
            if ((minutes >= 60) && hasHours) {
                hours = hours + 1;
                minutes = minutes - 60;
                if ((hours >= 24) && hasDays) {
                    days = days + 1;
                    hours = hours - 24;
                }
            }
        }
        secs = secs + 0; // avoid lint warning
    }
    const d = (hasDays && (days || opt.short)) ? days : '';
    // hours show normally OR as '0' filler when days are rendered and a later unit follows
    let h = (hasHours && (hours || opt.short)) ? hours : '';
    if (h === '' && hasHours && (d !== '') && (minutes || secs)) {
        h = '0';
    }
    const m = (hasMinutes ? (minutes ? minutes : (((d !== '') || h) && secs ? '0' : '')) : '');
    const s = (hasSeconds && secs ? secs : '');
    let res = '';
    const spacer = opt.short ? ':' : ' ';
    let decimalOut = 0;
    if (d != '') {
        decimalOut += d * 24;
        res = res + (opt.short && (d < 10) ? '0' + d : d) + (opt.short ? '' : 'd');
        if ((h != '') || (m != '') || (s != '')) {
            res = res + spacer;
        }
    } else if (opt.short && hasDays) {
        res = res + '00';
        if ((h != '') || (m != '') || (s != '')) {
            res = res + spacer;
        }
    }
    if (h != '') {
        decimalOut += parseFloat(h);
        res = res + (opt.short && (h < 10) ? '0' + h : h) + (opt.short ? '' : 'h');
        if (m != '') {
            res = res + spacer;
        }
    } else if (opt.short) {
        res = res + '00';
        if (m != '') {
            res = res + spacer;
        }
    }
    if (m != '') {
        decimalOut += (m / 60);
        res = res + (opt.short && (m < 10) ? '0' + m : m) + (opt.short ? '' : 'min');
        if ((s != '') && !opt.decimal) {
            res = res + spacer;
        }
    } else if (opt.short) {
        res = res + spacer + '00';
        if (s != '') {
            res = res + spacer;
        }
    }
    if (s != '') {
        decimalOut += (s / 3600);
        res = res + (opt.short && (s < 10) ? '0' + s : s) + (opt.short ? '' : 's');
    }
    if (res && opt.short) {
        res += hasDays ? 'd' : (hasHours ? 'h' : (hasMinutes ? 'min' : 's'));
    }
    let unit = 'h';
    if (opt.decimal !== false && decimalOut > 0) {
        res = parseFloat(decimalOut).toFixed(opt.decimal);
        if (res == 0) {
            unit = 'min';
            decimalOut = parseFloat(m) + parseFloat(s);
            res = parseFloat(decimalOut).toFixed(opt.decimal);
        }
        res = (res + '').replace('.', ',') + unit;
    }
    return negative + (res ? res : opt.displayOnEmpty);
};
export const formatDateTime = (value, format = 'DD.MM.YYYY HH:mm:ss') => {
    return moment(value).format(format);
};
export const formatPrice = (value, opt = {}) => {
    return (opt.withEuro ?  '€ ' : '') + Number(value).toFixed(2).replace('.', ',');
};

export const formatPeriod = (period) => {
    const value = parseInt(period);
    period = [...period].reverse().join().substring(0,1);
    const map = {'s': 'seconds', 'i': 'minutes', 'h': 'hours', 'H': 'hours', 'd': 'days', 'w': 'weeks', 'm':'months', 'y':'years'};
    return value + ' ' + trans('global.period.' + map[period]);
};
// options: {linkText: '', target: '_blank', 'linkOnly': false, 'useIcon': false, 'addIcon': false}
// -> PHP version: HelperService::replaceLink
export const linkify = (inputText, options = {}) => {
    let replacedText;
    const size = options.size ?? 24;
    const iconSpan = '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" class="pointer mdi-open-in-new" style="--sx: 1; --sy: 1; --r: 0deg;"><path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"></path></svg>';
    let linkText = (typeof(options.linkText)==='undefined') ? '' : options.linkText;
    if (options.useIcon) {
        linkText = iconSpan;
    }
    const target = options.target ? options.target : '_blank';

    if (inputText == null) {
        return null;
    }
    //URLs starting with http://, https://, or ftp://
    const replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim;
    replacedText = inputText.replace(replacePattern1,
        (!options.linkOnly && !options.useIcon? '$1 ' : '') +
            (options.useIcon ? '<a href="$1" target="'+target+'">'+linkText+'</a>' : 
                '<a href="$1" target="'+target+'">'+((linkText=='')?'$1':linkText)+(options.addIcon?' ' + iconSpan:'')+'</a>'));

    //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
    const replacePattern2 = /(^|[^/])(www\.[\S]+(\b|$))/gim;
    replacedText = replacedText.replace(replacePattern2,
        options.linkOnly ? 'https://$2' : 
            (options.useIcon ? '$1$2 <a href="https://$2" target="'+target+'">'+linkText+'</a>' : 
                '$1<a href="https://$2" target="'+target+'">'+((linkText=='')?'$2':linkText)+(options.addIcon?' ' + iconSpan:'')+'</a>'));

    //Change email addresses to mailto:: links.
    const replacePattern3 = /(([a-zA-Z0-9\-_.])+@[a-zA-Z_]+?(\.[a-zA-Z]{2,6})+)/gim;
    replacedText = replacedText.replace(replacePattern3,
        options.linkOnly ? '$1' : 
            (options.useIcon ? '$1 <a href="mailto:$1">'+linkText+'</a>' : 
                '<a href="mailto:$1">$1'+(options.addIcon?' ' + iconSpan:'')+'</a>'));
    
    return replacedText;
};

export const debounce = (fn, wait) => {
    let timer;
    return function(...args){
        if(timer) {
            clearTimeout(timer);
        }
        const context = this;
        timer = setTimeout(() => { fn.apply(context, args); }, wait);
    };
};

export const throttle = (fn, wait) => {
    let throttled = false;
    return function(...args){
        if(!throttled){
            fn.apply(this, args);
            throttled = true;
            setTimeout(() => {
                throttled = false;
            }, wait);
        }
    };
};

export const arrayDiffByKey = (key, ...arrays) => {
    return [].concat(...arrays.map( (arr, i) => {
        const others = arrays.slice(0);
        others.splice(i, 1);
        const unique = [...new Set([].concat(...others))];
        return arr.filter( x =>
            !unique.some(y => x[key] === y[key]),
        );
    }));
};

function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
  
export function mergeDeep(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) {
                    Object.assign(target, { [key]: {} });
                } else {
                    target[key] = Object.assign({}, target[key]);
                }
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }
    return mergeDeep(target, ...sources);
}
  
/**
 * formats pretty file size 8kB, 1.2MB, ..
 * if format is given as: Bytes, kB, MB, GB it will display the value in this specific unit
 * eg: formatFileSize(239748632746, 'MB') -> 228642,1 MB
 * @param integer size 
 * @param string format 
 * @returns 
 */
export const formatFileSize = (size, format = '') => {
    const unit = ['bytes', 'kB', 'MB', 'GB', 'TB'];
    let count = 0;
    while ((size / 1024 > 1) && ((format === '') || (format != unit[count]))) {
        size = size / 1024;
        count++;
    }
    return size.toFixed(1) + ' ' + unit[count];
};

export const pluck = (arr, ...keys) => {
    return keys.length > 1 ?
        arr.map(i => keys.map(k => i[k])) :
        arr.map(i => i[keys[0]]);
};

export const pluckFlat = (arrayOfObj, company_id = 0) => {
    let result = [];
    arrayOfObj.map((obj) => {
        if (obj && (!company_id || (((obj['company_id']??0) == company_id)))) {
            if (obj['content_node']??null) {
                result.push(obj['content_node']['id']);
            } else if (obj['content_node_id']??0) {
                result.push(obj['content_node_id']);
            }
            if (obj.children && obj.children.length) {
                result = result.concat(pluckFlat(obj.children));
            }
        }
    });
    return result;
};

export const pluckFlatContentNodeForVideo = (arrayOfObj, company_id = 0) => {
    let result = [];
    arrayOfObj.map((obj) => {
        if (obj) {
            company_id = parseInt(company_id??0);
            const nodeCompanyId = obj['content_node'] ? parseInt(obj['content_node']['company_id']??0) : 0;
            if ((company_id == 0) || (nodeCompanyId == 0) || (nodeCompanyId == company_id)) {
                if (obj['content_node']??null) {
                    if (obj['content_node']['videos']?.length??0) {
                        result.push(obj['content_node']['id']);
                    }
                } else if (obj['content_node_id']??0) {
                    result.push(obj['content_node_id']);
                }
            }
            if (obj.children && obj.children.length) {
                result = result.concat(pluckFlatContentNodeForVideo(obj.children, company_id));
            }
        }
    });
    return result;
};

/** Inertia may JSON-encode a PHP Collection as an array or as an object. */
export const listFromAuthCollection = (value) => {
    if (!value) {
        return [];
    }
    return Array.isArray(value) ? value : Object.values(value);
};

export const hasPermission = (permissions, name) => {
    return listFromAuthCollection(permissions).includes(name);
};

export const hasAnyRole = (roles, names) => {
    const list = listFromAuthCollection(roles);
    return names.some((name) => list.includes(name));
};

const asNumericIds = (value) => {
    return listFromAuthCollection(value).map((item) => {
        if (item !== null && typeof item === 'object') {
            return Number(item.team_id ?? item.location_id ?? item.id ?? item.value);
        }
        return Number(item);
    }).filter((id) => !Number.isNaN(id));
};

export const checkCompanyLocationAccess = (auth, location) => {
    if (hasAnyRole(auth?.roles, ['integeri.admin', 'company.admin'])) {
        return true;
    }
    const locationId = Number(location?.id);
    if (hasAnyRole(auth?.roles, ['company.location.admin'])
        && asNumericIds(auth?.user?.locationAdmins).includes(locationId)) {
        return true;
    }
    if (hasAnyRole(auth?.roles, ['company.team.admin'])) {
        return listFromAuthCollection(auth?.user?.teamAdmins).some((element) => {
            if (element !== null && typeof element === 'object') {
                return Number(element.location_id) === locationId;
            }
            return false;
        });
    }
    return false;
};

export const checkCompanyTeamAccess = (auth, team) => {
    if (hasAnyRole(auth?.roles, ['integeri.admin', 'company.admin'])) {
        return true;
    }
    if (hasAnyRole(auth?.roles, ['company.location.admin'])
        && asNumericIds(auth?.user?.locationAdmins).includes(Number(team?.location_id))) {
        return true;
    }
    if (hasAnyRole(auth?.roles, ['company.team.admin'])
        && asNumericIds(auth?.user?.teamAdmins).includes(Number(team?.id))) {
        return true;
    }
    return false;
};

export const getMimeType = async (url, then) => {
    await fetch(url, {method: 'HEAD', mode: 'no-cors'}).then((res) => {
        const type = res.headers.get('Content-Type');
        if (then) then(type);
    }).catch((error) => {
        if (then) then('error ' + error.message);
    });
};

export const isImageUrl = async (url, allowedTypes = [], then = null) => {
    await getMimeType(url, function(type) {
        if (allowedTypes.length) {
            type = allowedTypes.includes(type) ? true : type;
        } else {
            type = type.startsWith('image') ? true : type;
        }
        if (then) {
            then(type);
        }
    });
};

export const isVideoUrl = async (url, allowedTypes = [], then = null) => {
    await getMimeType(url, function(type) {
        if (allowedTypes.length) {
            type = allowedTypes.includes(type) ? true : type;
        } else {
            type = type.startsWith('video') ? true : type;
        }
        if (then) {
            then(type);
        }
    });
};

export const evalCalendarPeriod = (period, period_op) => {
    let [since, until, op, date] = [null, null, null, null];
    // passthrough from url
    if (typeof period == 'string') {
        const a = period.split('.');
        date = moment(Date.parse(a[2]+'-'+a[1]+'-'+a[0])).format('yy-MM-DD');
        op = period_op??'exact';
    }
    // single from calendar
    if (period instanceof Date) {
        date = moment(period).format('yy-MM-DD');
        op = period_op??'exact';
    }
    // range from calendar
    if (period instanceof Array) {
        since = period[0] ? moment(period[0]).format('yy-MM-DD') : null;
        until = period[1] ? moment(period[1]).format('yy-MM-DD') : null;
        op = period_op??'exact';
    }
    return [since, until, op, date];
};

export const dummyAvatarSrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdj4OlY9h8ABBgCOpbRy0cAAAAASUVORK5CYII=';
export const mapLanguageFlags = (lang) => {
    const list = {'zh':'cn', 'cs':'cz', 'da':'dk', 'et':'ee', 'en':'gb', 'el':'gr', 'ja':'jp', 'ko':'kr', 'nb':'no', 'sv':'se', 'sl':'si', 'ar':'sy', 'uk':'ua'};
    return list[lang] ?? lang ?? 'de';
};

export const mdiPath = (icon) => {
    // if not given mdiChevronRight convert icon name to key (chevron-right -> mdiChevronRight)
    const key = icon.substring(0,3) === 'mdi' ? icon : 'mdi' + icon.split('-').map(x => x.charAt(0).toUpperCase() + x.slice(1)).join('');
    const i =  mdi[key];
    return typeof i === 'string' ? i : mdi['mdiWeb'];
};

export const dtRowPerPageOptions = (pagination) => {
    const list = [];
    if (pagination.total == 0) {
        return [0];
    }
    // dt-row-per-page-options
    [5,10,20,50].forEach(function (i) {
        if (pagination.total > i) {
            list.push(i);
        }
    });
    list.push(pagination.total);
    // ensure selection in combo after dynamic total
    if (pagination.per_page > pagination.total) {
        pagination.per_page = pagination.total;
    }
    return list;
};

/**
 * Dashboard title for operator/practice group instruction (GS).
 */
export function groupInstructionListTitle(group, courseTitle = '') {
    const giTraining = group?.notes?.gi_training;
    if (giTraining !== 'operator' && giTraining !== 'practice') {
        return null;
    }
    const machineType = String(group?.notes?.machine_type ?? '').toLowerCase();
    let machine = '';
    if (machineType) {
        const translated = trans('machine.type.' + machineType);
        machine = (translated && translated !== ('machine.type.' + machineType)) ? translated : machineType;
    }
    if (!machine) {
        machine = courseTitle || '';
    }
    const key = giTraining === 'practice'
        ? 'course.groupassignment.label.gi_list_title_practice'
        : 'course.groupassignment.label.gi_list_title_theory';
    return trans(key, { args: { machine } });
}